import React from 'react'
import {graphql} from 'react-apollo'
import {get, first, findIndex, find, isEmpty, includes, filter, flowRight as compose, isEqual} from 'lodash'
import {withRouter, Link, Redirect} from 'react-router-dom'
import withStyles from '@material-ui/core/styles/withStyles'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import {withNamespaces, Trans} from 'react-i18next'
import {Loading} from '../../Common/Loading'
import PageTitle from '../../Common/PageTitle'
import PageBody from '../../Common/PageBody'
import messages from '../../../assets/messages'
import {ACCOUNTS_QUERY, CLIENT_DATA_QUERY} from '../../../graphql/queries'
import ForexRoutes from './routes'
import {isMobile} from '../../../common/utils/browser'
import {getItem, hasWebTrader} from '../../../common/utils'
import ForexTradeNow from './ForexTradeNow'
import Typography from '@material-ui/core/Typography'
import {accountTypes} from '@bdswiss/common-enums'
import {
  canBeBrokereeCopyTradingFollower,
  getAccountLabel,
  getTauroFactorisationFromAccount,
  isMt5ForexAccount,
} from '../../../common/utils/accounts'
import {config} from '../../../config'
import AppContext from '../../Common/contexts/AppContext'
import {backendGetRequest, getStrategiesDetails} from '../../../common/utils/requests'
import {InnerAppContext} from '../../../common/types'
import {State, BrokereeCopyTradingOffer} from './types'

const styles = theme => ({
  tradeNowButton: {
    marginTop: 6
  },
  // The same 10x chip the account tile carries, so the account keeps its marking when the
  // client opens it (PT-6510).
  tauroBadge: {
    backgroundColor: '#FDF0DC',
    borderRadius: 4,
    color: '#8A5A00',
    fontWeight: 500,
    marginLeft: 8,
    padding: '2px 8px',
    whiteSpace: 'nowrap' as const
  }
})

const getBrokereeCopyTradingOffers = async (accountId: string): Promise<{offers: BrokereeCopyTradingOffer[]}> => {
  const response = await backendGetRequest(`/api/v2/dashboard/accounts/${accountId}/providers/brokeree/offers`, true)

  if (!response.ok) {
    const errorText = await response.text()
    // eslint-disable-next-line no-console
    console.error(`Failed to fetch offers ${response.status} ${errorText}`)
    return {offers: []}
  }

  return await response.json()
}

class ForexAccountView extends React.Component<any,State> {
  static contextType = AppContext
  context!: InnerAppContext

  constructor(props) {
    super(props)
    this.onBrokereeCopyTradingSubscriptionUpdated = this.onBrokereeCopyTradingSubscriptionUpdated.bind(this)
    this.onBrokereeCopyTradingProviderUpdated = this.onBrokereeCopyTradingProviderUpdated.bind(this)
    this.fetchBrokereeCopyTradingOffers = this.fetchBrokereeCopyTradingOffers.bind(this)
    this.state = {
      strategies: {},
      brokereeCopyTradingOffers: [],
      brokereeCopyTradingOffersLoading: true
    } as State
  }

  handleChange(tabs, value) {
    const {history, match} = this.props
    const currentTab = filter(tabs, (tab) => !tab.hidden)[value]
    history.push(`${match.url}${currentTab.path}`)
  }

  closeDialog() {
    const {history} = this.props
    history.location.state && history.location.state.from && history.location.state.from==='/login' ?
      this.context.logout() : history.push('/accounts')
  }

  fetchBrokereeCopyTradingOffers(): void {
    const {account, viewer} = this.props

    if (!canBeBrokereeCopyTradingFollower(account, viewer)) {
      this.setState({brokereeCopyTradingOffers: [], brokereeCopyTradingOffersLoading: false})
      return
    }

    this.setState({brokereeCopyTradingOffersLoading: true})

    getBrokereeCopyTradingOffers(account.id).then((res) => {
      this.setState({brokereeCopyTradingOffers: res.offers, brokereeCopyTradingOffersLoading: false})
    })
  }

  onBrokereeCopyTradingSubscriptionUpdated(): void {
    this.props.refetchAccounts()
  }

  onBrokereeCopyTradingProviderUpdated(): void {
    this.props.refetchAccounts()
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.account, prevProps.account)) {
      this.fetchBrokereeCopyTradingOffers()
    }
  }

  componentDidMount() {
    const {account} = this.props
    getStrategiesDetails(account.id).then((res) => {
      this.setState({strategies: res})
    })
    this.fetchBrokereeCopyTradingOffers()
  }

  render() {
    const {location, match, loading, viewerLoading, account, accounts, account:{competitions}, classes, history, t, viewer: {company, locale, address}, match:{url}, viewer} = this.props
    const {showTab, key, showMT4DownloadLink, downloadTrader, featuresConfig:{copyTradingEnabled, competitions: competitionsAllowed}, common:{globalCopyTradingEnabled}} = config

    if (loading || viewerLoading || !account) return <Loading />
    const accountType = accountTypes[account.__typename]
    const label = getAccountLabel(account, locale)
    const tauroFactorisation = getTauroFactorisationFromAccount(account)
    const downloadTraderLabel = accountType && downloadTrader[accountType.category]
      ? downloadTrader[accountType.category].shortLabel : downloadTrader.default.shortLabel
    if (accountType.subscription && isEmpty(account.subscription) && location.pathname.indexOf('plan') === -1)
      return <Redirect to={{pathname: `/accounts/${account.id}/plan/payment`}}/>
    const hasSubscription =  !isEmpty(account.subscription)
    const showTradeNow = accountType.subscription ? hasSubscription : true
    const accountSubtype = get(account,'accountSubtype')
    const checkAccountCopyTrading = includes(get(copyTradingEnabled,'accountSubTypes',[]), accountSubtype) &&
    includes(get(copyTradingEnabled,'accountTypesCategory',[]), accountType.category) && !account.isDemo
    && !account.isViewOnly && !account.isArchived && isEmpty(find(get(account,'competitions',[]), 'active'))

    let enableCopyTrading = !isEmpty(this.state.strategies) && checkAccountCopyTrading

    if (enableCopyTrading) {
      const providerType = get(this.state.strategies[0], 'copyTradingProvider')
      enableCopyTrading = get(globalCopyTradingEnabled, providerType)
      || (!get(globalCopyTradingEnabled, providerType) && get(account.copyTrading,'isActive'))
    }

    const isCopyTradingTabShown = enableCopyTrading || this.state.brokereeCopyTradingOffers.length > 0
    const currentOfferSubscription = this.state.brokereeCopyTradingOffers.find((offer) => offer.isSubscribed) ?? null

    const showCompetitions = competitionsAllowed && competitions.length > 0 && !includes(get(competitionsAllowed, 'blockedCountries'), get(address, 'country', '')?.toUpperCase())
    const showProviderStats = get(account, 'showProviderStats')
    const showBrokereeProviderStats = Boolean(get(account, 'showBrokereeProviderStats'))
    const hasAccessToBrokereeAdvancedSettings = Boolean(get(account, 'hasAccessToBrokereeAdvancedSettings'))
    let tabs
    if (account.isArchived || account.isViewOnly) {
      tabs = [
        {path: '', label: t(messages.overview.i18nKey, messages.overview.defaults)},
        {path: '/funded', label: t(messages.fundedTab.i18nKey, messages.fundedTab.defaults), hidden: tauroFactorisation == null},
        {path: '/transactions/history', label: t(messages.payments.i18nKey, messages.payments.defaults)},
        // PT-7722: an archived account keeps Settings for the delete action.
        {path: '/settings', label: t(messages.settings.i18nKey, messages.settings.defaults), hidden: !account.isArchived},
      ]
    } else {
      tabs = [
        {path: '', label: t(messages.overview.i18nKey, messages.overview.defaults)},
        {path: '/funded', label: t(messages.fundedTab.i18nKey, messages.fundedTab.defaults), hidden: tauroFactorisation == null},
        {path: '/transactions', label: t(messages.payments.i18nKey, messages.payments.defaults), hidden: !showTab?.transactions},
        {path: '/downloadTrader', label: downloadTraderLabel, hidden: !showTab?.downloadTrader},
        {path: '/copyTrading', label: t(messages.copyTrading.i18nKey, messages.copyTrading.defaults), hidden: !isCopyTradingTabShown},
        {path: '/tradingCompetitions', label: t(messages.tradingCompetitions.i18nKey, messages.tradingCompetitions.defaults), hidden: !showCompetitions},
        {path: '/providerStats', label: t(messages.providerStats.i18nKey, messages.providerStats.defaults), hidden: !showProviderStats || showBrokereeProviderStats},
        {path: '/brokereeProviderStats', label: t(messages.providerStats.i18nKey, messages.providerStats.defaults), hidden: !showBrokereeProviderStats},
        {path: '/settings', label: t(messages.settings.i18nKey, messages.settings.defaults)},
        {path: '/plan', label: t(messages.plan.i18nKey, messages.plan.defaults), hidden: !hasSubscription},
      ]
    }

    const currentTab =
      find(tabs, (tab) => tab.path.length > 0 && !tab.hidden && location.pathname.indexOf(`${match.url}${tab.path}`) > -1) ||
        first(tabs)
    const currentTabIndex = findIndex(filter(tabs, (t) => !t.hidden), currentTab)

    const fxmt4SupportedCountries = ['bdSwissMauritius', 'tauroMarkets']

    return (<React.Fragment>
      <PageTitle
        title={label}
        onBack={() => this.closeDialog()}
        rightAction={
          <div className={classes.tradeNowButton}>
            {!isMt5ForexAccount(account.__typename) && hasWebTrader() && showTradeNow && <ForexTradeNow type='button' account={account} company={company} locale={locale || getItem('locale', 'en')} />}
            {showMT4DownloadLink && <Link to={`${url}/downloadTrader`}>
              <Typography variant="body2" color="primary" align='center'>
                <Trans {...messages.downloadMT4} values={{product:downloadTraderLabel}}/>
              </Typography>
            </Link>}
            {fxmt4SupportedCountries.includes(key) && config.downloadTrader.forexMt4.Links.web && <a href={config.downloadTrader.forexMt4.Links.web} target="_blank" rel="noreferrer">
              <Typography variant="body2" color="primary" align='center'><Trans {...messages.webtraderMT4}/></Typography>
            </a>}
          </div>
        }
        subtitle={
          <React.Fragment>
            <Trans {...messages.accountID} values={{accountId: account.remoteId}} />
            {tauroFactorisation?.multiplier != null && (
              <span className={classes.tauroBadge}>
                <Trans
                  {...messages.tauroFactorisedBadge}
                  values={{multiplier: tauroFactorisation.multiplier}}
                />
              </span>
            )}
          </React.Fragment>
        }
      >
        <Tabs
          variant={isMobile()? 'scrollable' : 'standard'}
          value={currentTabIndex}
          onChange={(_, i) => this.handleChange(tabs, i)}
          classes={{root: classes.tabsRoot}}>
          {tabs.map((tab, key) => !tab.hidden &&
            <Tab
              key={key}
              label={tab.label}
              textColor="inherit"
              onClick={() => history.push(`/accounts/${account.id}${tab.path}`)} />
          )}
        </Tabs>
      </PageTitle>
      <PageBody>
        <ForexRoutes
          client={viewer}
          account={account}
          accounts={accounts}
          currentOfferSubscription={currentOfferSubscription}
          brokereeCopyTradingOffers={this.state.brokereeCopyTradingOffers}
          brokereeCopyTradingOffersLoading={this.state.brokereeCopyTradingOffersLoading}
          showBrokereeProviderStats={showBrokereeProviderStats}
          canBeBrokereeCopyTradingFollower={canBeBrokereeCopyTradingFollower(account, viewer)}
          hasAccessToBrokereeAdvancedSettings={hasAccessToBrokereeAdvancedSettings}
          onSubscriptionUpdated={this.onBrokereeCopyTradingSubscriptionUpdated}
          onBrokereeProviderUpdated={this.onBrokereeCopyTradingProviderUpdated}
          onBrokereeOffersRefetch={this.fetchBrokereeCopyTradingOffers}
        />
      </PageBody>
    </React.Fragment>)
  }
}

export default compose(
  withNamespaces(),
  withRouter,
  withStyles(styles, {withTheme: true}),
  graphql(ACCOUNTS_QUERY, {
    props: ({data: {error, loading}, data, ownProps: {match}}: any) => {
      const {accountId} = match.params
      const account = get(data, 'viewer.accounts', []).find(account => account.id === Number(accountId))
      return {
        error,
        loading,
        account,
        accounts: get(data, 'viewer.accounts', []),
        refetchAccounts: data.refetch
      }
    }
  }),
  graphql(CLIENT_DATA_QUERY, {
    props: ({data: {error, loading: viewerLoading}, data}: any) => ({
      error,
      viewerLoading,
      viewer: get(data, 'viewer'),
    })
  })
)(ForexAccountView)
